import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  searchLists, addListEntry, removeListEntry, ListEntry, LIST_KIND,
} from '../services/entitiesService';
import { COUNTRY_LIST, countryFlag } from '../v2/countryList';
import { iso2ToNumeric, numericToIso2 } from '../countryCodes';
import './WatchlistAdminOverlay.css';

/**
 * WatchlistAdminOverlay
 *
 * Compliance list administration surface.  Two scopes of data:
 *
 *   Global   — visible to main admin only.  Denies anywhere, applied
 *              before country rules.
 *   Country  — visible to the main admin (for any country) and to the
 *              country admin for their assigned country/ies only.
 *              Main admin sees every country; country admin sees the
 *              intersection of `allowedCountries` and the countries the
 *              server grants write access to.
 *
 * The server is the source of truth for authorisation on every mutation
 * — this component only uses the props to render the right choices.
 */

export interface WatchlistAdminOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  /** Countries the caller is the country-admin of (numeric ISO-3166 codes). */
  allowedCountries: number[];
  /** True iff the caller is the main protocol admin. */
  canEditGlobal: boolean;
  /** Default country (numeric) to pre-select when the overlay opens. */
  defaultCountry?: number;
}

type TabKey = 'global' | 'country';

interface CountryOption { code: number; iso2: string; name: string }

const TABS: { id: TabKey; label: string; icon: string; shortLabel: string }[] = [
  { id: 'global',  label: 'Global Lists',  icon: '🌐', shortLabel: 'Global'  },
  { id: 'country', label: 'Country Lists', icon: '🗺️', shortLabel: 'Country' },
];

const WatchlistAdminOverlay: React.FC<WatchlistAdminOverlayProps> = ({
  isOpen, onClose, allowedCountries, canEditGlobal, defaultCountry,
}) => {
  // ── Scope tabs ───────────────────────────────────────────────────────────
  // Main admin sees both tabs.  Country admins see only "Country".
  const [tab, setTab] = useState<TabKey>(canEditGlobal ? 'global' : 'country');

  // ── Country dropdown ─────────────────────────────────────────────────────
  // Main admin can target ANY country; country admins only the ones they
  // are explicitly allowed to edit.
  const countryOptions = useMemo<CountryOption[]>(() => {
    if (canEditGlobal) {
      const rows: CountryOption[] = [];
      for (const c of COUNTRY_LIST) {
        const code = iso2ToNumeric(c.iso2);
        if (code > 0) rows.push({ code, iso2: c.iso2, name: c.name });
      }
      rows.sort((a, b) => a.name.localeCompare(b.name));
      return rows;
    }
    return allowedCountries
      .map((code) => {
        const iso2 = numericToIso2(code);
        const name = COUNTRY_LIST.find((c) => c.iso2 === iso2)?.name || iso2 || String(code);
        return { code, iso2: iso2 || '', name };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [canEditGlobal, allowedCountries]);

  const [country, setCountry] = useState<number | null>(() => {
    if (defaultCountry != null && countryOptions.some((c) => c.code === defaultCountry)) {
      return defaultCountry;
    }
    return countryOptions[0]?.code ?? null;
  });

  useEffect(() => {
    if (!isOpen) return;
    if (country == null && countryOptions[0]) setCountry(countryOptions[0].code);
  }, [isOpen, country, countryOptions]);

  // If the caller isn't a main admin and has no country assignments, force
  // the tab to "country" and show the empty state.
  useEffect(() => {
    if (!canEditGlobal && tab === 'global') setTab('country');
  }, [canEditGlobal, tab]);

  // ── Filters ──────────────────────────────────────────────────────────────
  const [kind, setKind] = useState<'any' | 'watch' | 'deny'>('any');
  const [query, setQuery] = useState('');

  // ── Data ─────────────────────────────────────────────────────────────────
  const [entries, setEntries] = useState<ListEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // ── Add form ─────────────────────────────────────────────────────────────
  const [newSubject, setNewSubject] = useState('');
  const [newReason, setNewReason] = useState('');
  const [newKind, setNewKind] = useState<1 | 2>(LIST_KIND.WATCH);
  const [adding, setAdding] = useState(false);

  const refresh = useCallback(async () => {
    if (tab === 'country' && country == null) { setEntries([]); return; }
    setLoading(true);
    setError(null);
    try {
      const params: { country?: number | 'global'; kind?: number; q?: string } = {};
      if (tab === 'global') params.country = 'global';
      else params.country = country!;
      if (kind !== 'any') params.kind = kind === 'deny' ? LIST_KIND.DENY : LIST_KIND.WATCH;
      if (query) params.q = query;
      const rows = await searchLists(params);
      setEntries(rows);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'load_failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [tab, country, kind, query]);

  useEffect(() => {
    if (!isOpen) return;
    refresh();
  }, [isOpen, refresh]);

  const handleAdd = useCallback(async () => {
    if (!newSubject.trim()) { setError('subject_required'); return; }
    if (tab === 'country' && country == null) { setError('country_required'); return; }
    setError(null); setNotice(null); setAdding(true);
    try {
      if (tab === 'global') {
        await addListEntry('global', newSubject.trim(), { kind: newKind, reason: newReason.trim() });
      } else {
        await addListEntry('country', newSubject.trim(), {
          kind: newKind, reason: newReason.trim(), country: country!,
        });
      }
      setNewSubject(''); setNewReason('');
      setNotice(newKind === LIST_KIND.DENY ? 'Added to deny-list.' : 'Added to watch-list.');
      refresh();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'add_failed';
      setError(msg);
    } finally {
      setAdding(false);
    }
  }, [tab, country, newSubject, newReason, newKind, refresh]);

  const handleRemove = useCallback(async (entry: ListEntry) => {
    setError(null); setNotice(null);
    try {
      if (entry.scope === 'global') await removeListEntry('global', entry.subject);
      else await removeListEntry('country', entry.subject, entry.country);
      setNotice('Removed.');
      refresh();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'remove_failed';
      setError(msg);
    }
  }, [refresh]);

  if (!isOpen) return null;

  const activeCountryLabel = tab === 'country' && country != null
    ? (() => {
        const row = countryOptions.find((c) => c.code === country);
        return row ? `${countryFlag(row.iso2)} ${row.name}` : String(country);
      })()
    : null;

  const shortSubject = (s: string) =>
    s.length > 28 ? `${s.slice(0, 10)}…${s.slice(-6)}` : s;

  return (
    <div className="overlay-backdrop" onClick={onClose}>
      <div className="watchlist-overlay" onClick={(e) => e.stopPropagation()}>
        {/* ── Header ────────────────────────────────────────────────────── */}
        <div className="overlay-header">
          <div className="watchlist-title">
            <span className="watchlist-title-icon">🛡️</span>
            <div>
              <h2>Compliance Lists</h2>
              <div className="watchlist-subtitle">
                {canEditGlobal
                  ? 'Main admin — manage global and per-country watch / deny lists.'
                  : 'Country admin — manage your assigned country scope.'}
              </div>
            </div>
          </div>
          <button
            className="overlay-close-button"
            onClick={onClose}
            aria-label="Close"
          >×</button>
        </div>

        {/* ── Scope tabs ────────────────────────────────────────────────── */}
        <div className="watchlist-tabs" role="tablist">
          {TABS.map((t) => {
            const disabled = t.id === 'global' && !canEditGlobal;
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={tab === t.id}
                disabled={disabled}
                onClick={() => setTab(t.id)}
                className={`watchlist-tab ${tab === t.id ? 'active' : ''}`}
                title={disabled ? 'Only the main admin can edit the global list' : undefined}
              >
                <span className="tab-icon">{t.icon}</span>
                <span className="tab-label-full">{t.label}</span>
                <span className="tab-label-short">{t.shortLabel}</span>
              </button>
            );
          })}
        </div>

        {/* ── Body ──────────────────────────────────────────────────────── */}
        <div className="watchlist-content">
          {/* Country picker — only on Country tab */}
          {tab === 'country' && (
            <div className="watchlist-country-row">
              <label className="watchlist-label">Country</label>
              {countryOptions.length === 0 ? (
                <div className="watchlist-empty-inline">
                  You don&rsquo;t administer any country yet. Ask the main admin to add you as a
                  country admin, or switch to the Global tab.
                </div>
              ) : (
                <select
                  className="watchlist-select watchlist-select-country"
                  value={country ?? ''}
                  onChange={(e) => setCountry(Number(e.target.value))}
                >
                  {countryOptions.map((c) => (
                    <option key={c.code} value={c.code}>
                      {countryFlag(c.iso2)} {c.name}
                      {canEditGlobal ? '' : ' (assigned)'}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Scope summary chip */}
          <div className="watchlist-scope-chip">
            {tab === 'global' ? (
              <><span className="dot dot-global" /> Editing the <strong>Global</strong> list — applies to all countries.</>
            ) : country != null ? (
              <><span className="dot dot-country" /> Editing <strong>{activeCountryLabel}</strong> list — applies only to this country.</>
            ) : (
              <><span className="dot dot-disabled" /> Select a country to begin.</>
            )}
          </div>

          {/* ── Add form ─────────────────────────────────────────────── */}
          <div className="watchlist-add-card">
            <div className="watchlist-card-title">Add entry</div>
            <div className="watchlist-add-grid">
              <div className="field field-subject">
                <label className="watchlist-label">Wallet / entity ID</label>
                <input
                  className="watchlist-input"
                  placeholder="e.g. GDPM… or entity UUID"
                  value={newSubject}
                  onChange={(e) => setNewSubject(e.target.value)}
                  spellCheck={false}
                  autoCapitalize="none"
                />
              </div>
              <div className="field field-reason">
                <label className="watchlist-label">Reason (optional)</label>
                <input
                  className="watchlist-input"
                  placeholder="e.g. sanctions screening hit"
                  value={newReason}
                  onChange={(e) => setNewReason(e.target.value)}
                />
              </div>
              <div className="field field-kind">
                <label className="watchlist-label">Kind</label>
                <div className="watchlist-kind-toggle">
                  <button
                    className={`kind-chip ${newKind === LIST_KIND.WATCH ? 'active watch' : ''}`}
                    onClick={() => setNewKind(LIST_KIND.WATCH as 1)}
                    type="button"
                  >👁️ Watch</button>
                  <button
                    className={`kind-chip ${newKind === LIST_KIND.DENY ? 'active deny' : ''}`}
                    onClick={() => setNewKind(LIST_KIND.DENY as 2)}
                    type="button"
                  >⛔ Deny</button>
                </div>
              </div>
              <div className="field field-submit">
                <button
                  className="watchlist-btn watchlist-btn-primary"
                  onClick={handleAdd}
                  disabled={adding || !newSubject.trim() || (tab === 'country' && country == null)}
                >
                  {adding ? 'Adding…' : 'Add'}
                </button>
              </div>
            </div>
          </div>

          {/* ── Filters ──────────────────────────────────────────────── */}
          <div className="watchlist-filters">
            <div className="watchlist-filter-group">
              <button
                className={`filter-chip ${kind === 'any'   ? 'active' : ''}`}
                onClick={() => setKind('any')}
                type="button"
              >All</button>
              <button
                className={`filter-chip ${kind === 'watch' ? 'active watch' : ''}`}
                onClick={() => setKind('watch')}
                type="button"
              >👁️ Watch</button>
              <button
                className={`filter-chip ${kind === 'deny'  ? 'active deny' : ''}`}
                onClick={() => setKind('deny')}
                type="button"
              >⛔ Deny</button>
            </div>
            <div className="watchlist-search">
              <span className="watchlist-search-icon">🔎</span>
              <input
                className="watchlist-input"
                placeholder="Search subject or reason…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          </div>

          {/* ── Status ───────────────────────────────────────────────── */}
          {notice && <div className="watchlist-notice">{notice}</div>}
          {error && <div className="watchlist-error">Error: {error}</div>}

          {/* ── List ─────────────────────────────────────────────────── */}
          <div className="watchlist-entries">
            {loading ? (
              <div className="watchlist-empty">Loading…</div>
            ) : entries.length === 0 ? (
              <div className="watchlist-empty">
                {tab === 'country' && country == null
                  ? 'Select a country to view its entries.'
                  : 'No entries match the current filters.'}
              </div>
            ) : (
              entries.map((e) => {
                const iso2 = e.country != null ? numericToIso2(e.country) : '';
                return (
                  <div key={`${e.scope}:${e.country ?? 'g'}:${e.subject}`} className="watchlist-row">
                    <div className={`watchlist-kind-pill ${e.kind === LIST_KIND.DENY ? 'deny' : 'watch'}`}>
                      {e.kind === LIST_KIND.DENY ? '⛔ Deny' : '👁️ Watch'}
                    </div>
                    <div className="watchlist-row-main">
                      <div className="watchlist-row-subject" title={e.subject}>
                        <span className="mono">{shortSubject(e.subject)}</span>
                        <button
                          className="watchlist-copy-btn"
                          onClick={() => { try { navigator.clipboard.writeText(e.subject); } catch { /* ignore */ } }}
                          title="Copy full subject"
                          type="button"
                        >⧉</button>
                      </div>
                      <div className="watchlist-row-meta">
                        <span className="meta-chip">
                          {e.scope === 'global' ? '🌐 Global' : `${iso2 ? countryFlag(iso2) : '🏳️'} ${iso2 || e.country}`}
                        </span>
                        <span className="meta-chip">{new Date(e.addedAt).toLocaleString()}</span>
                        {e.reason ? <span className="meta-chip meta-reason" title={e.reason}>{e.reason}</span> : null}
                      </div>
                    </div>
                    <button
                      className="watchlist-btn watchlist-btn-danger"
                      onClick={() => handleRemove(e)}
                      type="button"
                    >Remove</button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WatchlistAdminOverlay;
